// Components==============
import { faFlame } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslate } from 'hooks/useTranslate';
import React, { useRef, useState } from 'react';
import { getSmartImage } from 'utilities/getSmartImage';
import Button from '../Button/Button';
import Input from '../Form/Input';
import Modal from '../Modal/Modal';
import './UploadModal.module.scss';
// =========================

export default function UploadModal({
  setModalIsOpen,
  handleUpload,
}: {
  setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleUpload: (file: File) => void;
}) {
  const labelRef = useRef<HTMLLabelElement>(null);

  const translate = useTranslate();

  const [localFile, setLocalFile] = useState<File | null>(null);

  const [image, setImage] = useState({ path: '', name: '' });
  const [inputValue, setInputValue] = useState('');
  const [isDroppable, setIsDroppable] = useState(false);

  function uploadLocalImage(file: File) {
    if (file) setLocalFile(file);

    const imageReader = new FileReader();

    imageReader.addEventListener('load', () => {
      if (typeof imageReader.result === 'string') {
        setImage({ path: imageReader.result as string, name: file?.name! });
      }
    });

    if (file) imageReader.readAsDataURL(file);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]!;

    uploadLocalImage(file);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    if (inputValue) return;
    setIsDroppable(false);
    const file = e.dataTransfer.files?.[0];

    uploadLocalImage(file);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    if (!inputValue && !localFile) setIsDroppable(true);
    e.dataTransfer.dropEffect = 'link';
  }

  return (
    <Modal setModalIsOpen={setModalIsOpen} size="small-medium">
      <div styleName="wrapper">
        <input
          type="file"
          id="upload-btn"
          onChange={(e) => handleChange(e)}
          name="upload"
          styleName="upload"
        />
        <div styleName="content">
          <h2>{translate('general.terms.upload-work')}</h2>
          <div
            styleName={`drop-zone ${isDroppable ? 'is-droppable' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={() => setIsDroppable(false)}
            style={{
              backgroundImage: image.path
                ? `url(${getSmartImage(image.path, image.name)})`
                : '',
            }}
          >
            {!image.path && (
              <>
                <FontAwesomeIcon icon={faFlame} />
                <h3>Drop it like it's hot</h3>
              </>
            )}
          </div>
          <label
            htmlFor="upload-btn"
            ref={labelRef}
            style={{ pointerEvents: inputValue ? 'none' : 'all' }}
          >
            <Button
              onClick={() =>
                labelRef.current ? labelRef.current.click() : undefined
              }
              inactive={!!inputValue}
              color="grey"
            >
              {translate('general.terms.select-file')}
            </Button>
          </label>
          {!localFile && (
            <>
              <div>
                <h2>{translate('general.terms.external-storage-link')}</h2>
                <p styleName="description">
                  {translate('general.terms.external-link-description')}
                </p>
              </div>

              <Input
                name=""
                ext_formValue={inputValue}
                ext_onChange={(e) => setInputValue(e.target.value)}
                placeholder={translate(
                  'general.terms.external-storage-placeholder'
                )}
              />
            </>
          )}
          <div styleName="button-wrapper">
            <Button color="grey" onClick={() => setModalIsOpen(false)}>
              {translate('general.terms.cancel')}
            </Button>
            <Button
              onClick={() => {
                setModalIsOpen(false);
                // @ts-ignore
                handleUpload(inputValue || localFile!);
              }}
              inactive={!localFile && !inputValue}
            >
              {translate('general.terms.upload-work')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
